import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { walletAPI } from '../services/api';
import { WalletCard } from '../components/Wallet';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import { cn } from '../lib/utils';

function WalletManagement() {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    ownerType: '',
    active: true
  });
  const [systemStats, setSystemStats] = useState({
    totalSystemBalance: 0,
    walletCounts: {},
    transactionSummary: {}
  });

  useEffect(() => {
    loadWallets();
    loadSystemStats();
  }, [filters]);

  const loadWallets = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await walletAPI.getAll(filters);
      setWallets(response.data?.data?.wallets || []);
    } catch (err) {
      console.error('Error loading wallets:', err);
      setError('Failed to load wallets');
      setWallets([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSystemStats = async () => {
    try {
      const response = await walletAPI.getSystemStats();
      setSystemStats(response.data?.data?.statistics || {
        totalSystemBalance: 0,
        walletCounts: {},
        transactionSummary: {}
      });
    } catch (err) {
      console.error('Error loading system stats:', err);
      setSystemStats({
        totalSystemBalance: 0,
        walletCounts: {},
        transactionSummary: {}
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value === '' ? undefined : value
    }));
  };

  const handleRefresh = () => {
    loadWallets();
    loadSystemStats();
  };

  // Loading State
  if (loading && wallets.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-7xl mx-auto">
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64 mb-8" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="overflow-hidden">
                <CardContent className="p-6">
                  <Skeleton className="h-10 w-10 rounded-full mb-3" />
                  <Skeleton className="h-6 w-24 mb-2" />
                  <Skeleton className="h-4 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-6 w-32 mb-4" />
                  <Skeleton className="h-8 w-24 mb-2" />
                  <Skeleton className="h-4 w-full mb-4" />
                  <div className="flex gap-2">
                    <Skeleton className="h-9 w-24" />
                    <Skeleton className="h-9 w-20" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Back Navigation */}
        <Link
          to="/dashboard"
          className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-6 transition-colors"
        >
          ← Back to Dashboard
        </Link>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Wallet Management</h1>
            <p className="text-slate-600 mt-1">Manage member and tournament wallets</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleRefresh}>
              🔄 Refresh
            </Button>
            <Button asChild className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
              <Link to="/wallets/create">+ Create Wallet</Link>
            </Button>
          </div>
        </div>

        {/* System Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="relative overflow-hidden border-0 shadow-lg">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-teal-600 opacity-90" />
            <CardContent className="relative p-6 text-white">
              <div className="flex items-center gap-4">
                <div className="text-3xl">💰</div>
                <div>
                  <div className="text-2xl font-bold">{formatCurrency(systemStats.totalSystemBalance)}</div>
                  <div className="text-emerald-100 text-sm">Total System Balance</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border-0 shadow-lg">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 opacity-90" />
            <CardContent className="relative p-6 text-white">
              <div className="flex items-center gap-4">
                <div className="text-3xl">👥</div>
                <div>
                  <div className="text-2xl font-bold">{systemStats.walletCounts?.member || 0}</div>
                  <div className="text-blue-100 text-sm">Member Wallets</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border-0 shadow-lg">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-600 opacity-90" />
            <CardContent className="relative p-6 text-white">
              <div className="flex items-center gap-4">
                <div className="text-3xl">🏌️</div>
                <div>
                  <div className="text-2xl font-bold">{systemStats.walletCounts?.tournament || 0}</div>
                  <div className="text-purple-100 text-sm">Tournament Wallets</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border-0 shadow-lg">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500 to-orange-600 opacity-90" />
            <CardContent className="relative p-6 text-white">
              <div className="flex items-center gap-4">
                <div className="text-3xl">📊</div>
                <div>
                  <div className="text-2xl font-bold">
                    {Object.values(systemStats.transactionSummary || {}).reduce((sum, t) => sum + (t.count || 0), 0)}
                  </div>
                  <div className="text-amber-100 text-sm">Total Transactions</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-8 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label htmlFor="owner-type" className="text-sm font-medium text-slate-700">
                  Wallet Type:
                </label>
                <select
                  id="owner-type"
                  value={filters.ownerType || ''}
                  onChange={(e) => handleFilterChange('ownerType', e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">All Types</option>
                  <option value="member">Member Wallets</option>
                  <option value="tournament">Tournament Wallets</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <label htmlFor="active-status" className="text-sm font-medium text-slate-700">
                  Status:
                </label>
                <select
                  id="active-status"
                  value={filters.active === undefined ? '' : filters.active.toString()}
                  onChange={(e) => handleFilterChange('active', e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">All Statuses</option>
                  <option value="true">Active Only</option>
                  <option value="false">Inactive Only</option>
                </select>
              </div>

              <Button variant="secondary" asChild className="ml-auto">
                <Link to="/wallets/transfer">💸 Transfer Funds</Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Error State */}
        {error && (
          <Alert variant="destructive" className="mb-8">
            <AlertTitle>Error Loading Wallets</AlertTitle>
            <AlertDescription className="flex items-center justify-between">
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={handleRefresh}>
                Try Again
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Wallets Grid */}
        {!loading && !error && (
          <>
            {wallets.length === 0 ? (
              <Card className="text-center py-16 shadow-sm">
                <CardContent>
                  <div className="text-6xl mb-4">👛</div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Wallets Found</h3>
                  <p className="text-slate-600 mb-6">No wallets match your current filters.</p>
                  <Button asChild className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
                    <Link to="/wallets/create">Create Your First Wallet</Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {wallets.map((wallet) => (
                  <WalletCard
                    key={wallet._id}
                    wallet={wallet}
                    showActions={true}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Quick Actions */}
        <div className="mt-12">
          <h3 className="text-xl font-semibold text-slate-900 mb-6">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/wallets/reports" className="group">
              <Card className="h-full transition-all duration-200 hover:shadow-lg hover:border-blue-200 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="text-3xl mb-3 group-hover:scale-110 transition-transform">📊</div>
                  <h4 className="font-semibold text-slate-900 mb-1">View Reports</h4>
                  <p className="text-sm text-slate-600">Generate wallet and transaction reports</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/wallets/transfer" className="group">
              <Card className="h-full transition-all duration-200 hover:shadow-lg hover:border-emerald-200 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="text-3xl mb-3 group-hover:scale-110 transition-transform">💸</div>
                  <h4 className="font-semibold text-slate-900 mb-1">Transfer Funds</h4>
                  <p className="text-sm text-slate-600">Move money between wallets</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/members" className="group">
              <Card className="h-full transition-all duration-200 hover:shadow-lg hover:border-purple-200 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="text-3xl mb-3 group-hover:scale-110 transition-transform">👥</div>
                  <h4 className="font-semibold text-slate-900 mb-1">Manage Members</h4>
                  <p className="text-sm text-slate-600">View and manage member wallets</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/tournaments" className="group">
              <Card className="h-full transition-all duration-200 hover:shadow-lg hover:border-amber-200 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="text-3xl mb-3 group-hover:scale-110 transition-transform">🏌️</div>
                  <h4 className="font-semibold text-slate-900 mb-1">Tournament Wallets</h4>
                  <p className="text-sm text-slate-600">View tournament financial data</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WalletManagement;
